import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { get, set, over, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useTranslation } from 'react-i18next';
import FinancialProductDetailsInsurersTable from './FinancialProductInsurersTable';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useToast } from '@totalsoft/rocket-ui';
import { financialProductInsurerValidator } from 'features/financialProducts/validators';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { INSURANCE_CONVENTIONS_TYPES_QUERY, INSURANCE_TYPES_QUERY, INSURERS_QUERY } from 'features/financialProducts/edit/queries';
import { ADD_UPDATE_FINANCIAL_PRODUCT_INSURER, REMOVE_FINANCIAL_PRODUCT_INSURER } from 'features/financialProducts/edit/mutations';
import { FINANCIAL_PRODUCT_INSURER_UPDATED, FINANCIAL_PRODUCT_INSURER_DELETED } from 'features/financialProducts/edit/subscriptions';
import { remove } from 'ramda';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const FinancialProductDetailsInsurersContainer = ({ canWrite, isNewFinancialProduct, productInsurerList, productId }) => {
  const [t] = useTranslation();
  const addToast = useToast();
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);
  const [validation, validate] = useDirtyFieldValidation(financialProductInsurerValidator);

  // region Data fetching
  const { data: insuranceTypesData } = useQuery(INSURANCE_TYPES_QUERY);
  const insuranceTypeList = useMemo(() => insuranceTypesData?.insuranceTypes || [], [insuranceTypesData]);

  const { data: insurersListData } = useQuery(INSURERS_QUERY);
  const insurersList = useMemo(() => insurersListData?.insurersList || [], [insurersListData]);

  const { data: conventionsListData } = useQuery(INSURANCE_CONVENTIONS_TYPES_QUERY);
  const conventionsList = useMemo(() => conventionsListData?.lightConventions || [], [conventionsListData]);

  // endregion

  //region mutations and subscriptions
  const [deleteFinancialProductInsurer] = useMutation(REMOVE_FINANCIAL_PRODUCT_INSURER);

  useSubscription(FINANCIAL_PRODUCT_INSURER_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Insurers.InsurerDeleted'), 'success');
      const lensLocal = lens |> get;
      const objTL = lensLocal.filter(
        el => el.leasingFinancialProductXLeasInsurerId === data.financialProductInsurerDeleted.leasingFinancialProductXLeasInsurerId
      );
      lensLocal.splice(lensLocal.indexOf(objTL), 1);
      set(lens)(lensLocal);
      client.cache.evict({
        id: `FinancialProductItem:{"productId":${productId}}`,
        fieldName: 'financialProductInsurers'
      });
      client.cache.gc();
    }
  });

  useSubscription(FINANCIAL_PRODUCT_INSURER_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.Insurers.InsurerUpdated'), 'success');
      const lensLocal = [...(lens |> get)];
      lensLocal.forEach(element => {
        if (
          element.insurerId === data.financialProductInsurerUpdated.outputData.insurerId &&
          element.insuranceConventionId === data.financialProductInsurerUpdated.outputData.insuranceConventionId
        ) {
          element.leasingFinancialProductXLeasInsurerId =
            data.financialProductInsurerUpdated.outputData.leasingFinancialProductXLeasInsurerId;
          element.isInEditing = false;
          element.isSaving = false;
        }
      });
      set(lens)(lensLocal);
      client.cache.evict({
        id: `FinancialProductItem:{"productId":${productId}}`,
        fieldName: 'financialProductInsurers'
      });
    }
  });

  const [saveFinancialProductInsurer] = useMutation(ADD_UPDATE_FINANCIAL_PRODUCT_INSURER);
  //endregion mutations and subscriptions

  //region handlers
  const handleAdd = useCallback(() => {
    set(lens)([
      ...(lens |> get),
      {
        leasingFinancialProductXLeasInsurerId: null,
        productId: productId,
        insTypeId: null,
        insuranceTypeName: '',
        insurerId: null,
        insurerName: '',
        insuranceConventionId: null,
        insuranceConventionName: '',
        implicit: false,
        mandatory: false,
        isInEditing: true
      }
    ]);
  }, [lens, productId]);

  const handleCancelEdit = useCallback(
    (leasingFinancialProductXLeasInsurerId, orderNo) => {
      if (!leasingFinancialProductXLeasInsurerId) set(lens)(remove(orderNo, 1, lens |> get));
      else {
        const original = productInsurerList.find(x => x.leasingFinancialProductXLeasInsurerId === leasingFinancialProductXLeasInsurerId);
        over(lens[orderNo], prevValue => ({ ...prevValue, ...original, isInEditing: false }));
      }
    },
    [lens, productInsurerList]
  );

  const handleDelete = useCallback(
    (leasingFinancialProductXLeasInsurerId, orderNo) => {
      if (!leasingFinancialProductXLeasInsurerId) set(lens)(remove(orderNo, 1, lens |> get));
      else if (window.confirm(t('FinancialProduct.Details.Insurers.DeleteInsurerConfirmation'))) {
        deleteFinancialProductInsurer({
          variables: {
            input: { leasingFinancialProductXLeasInsurerId }
          }
        });
      }
    },
    [deleteFinancialProductInsurer, lens, t]
  );

  const handleSave = useCallback(
    orderNo => {
      if (!validate(lens |> get)) return;
      validate(lens |> get, dirtyInfo);
      set(lens)([...(lens |> get)], {
        [orderNo]: {
          isSaving: true,
          isInEditing: false
        }
      });
      const elementTL = [...(lens |> get)][orderNo];
      saveFinancialProductInsurer({
        variables: {
          input: {
            leasingFinancialProductXLeasInsurerId: parseInt(elementTL.leasingFinancialProductXLeasInsurerId),
            productId: elementTL.productId,
            insTypeId: elementTL.insTypeId,
            insurerId: elementTL.insurerId,
            insuranceConventionId: elementTL.insuranceConventionId,
            implicit: elementTL.implicit,
            mandatory: elementTL.mandatory
          }
        }
      });
    },
    [lens, saveFinancialProductInsurer, validate, dirtyInfo]
  );

  //endregion handlers

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  // region effects

  useEffect(() => {
    resetLens(
      productInsurerList?.map(element => {
        const elementLocal = { ...element };
        elementLocal.isInEditing = false;
        elementLocal.isSaving = false;
        return elementLocal;
      }) || []
    );
  }, [resetLens, productInsurerList]);

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [dirtyInfo, lens, validate]);

  useGlobalIsDirty(isDirty);

  // endregion effects

  return (
    <FinancialProductDetailsInsurersTable
      canWrite={canWrite}
      isNewFinancialProduct={isNewFinancialProduct}
      onAdd={handleAdd}
      onCancelEdit={handleCancelEdit}
      onDelete={handleDelete}
      onSave={handleSave}
      productInsurerList={lens}
      insuranceTypeList={insuranceTypeList}
      insurersList={insurersList}
      conventionList={conventionsList}
      validation={validation}
    />
  );
};

FinancialProductDetailsInsurersContainer.propTypes = {
  productId: PropTypes.number.isRequired,
  isNewFinancialProduct: PropTypes.bool.isRequired,
  canWrite: PropTypes.bool.isRequired,
  productInsurerList: PropTypes.array.isRequired
};

export default FinancialProductDetailsInsurersContainer;
